import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, LayoutGrid, Table as TableIcon, Search, HardHat, Compass, ClipboardCheck, TrendingUp } from "lucide-react";

const STATUS_COLORS = {
    active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
    on_hold: "bg-amber-500/15 text-amber-500 border-amber-500/30",
    completed: "bg-navy/15 text-navy border-navy/30",
    archived: "bg-secondary text-muted-foreground border-border",
};

const NUMBER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function ProjectsList() {
    const nav = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("cards");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showNew, setShowNew] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/projects");
            setRows(data.items || []);
        } catch (e) {
            toast.error("Failed to load projects");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        let items = rows;
        if (statusFilter) items = items.filter((r) => r.status === statusFilter);
        if (q) {
            const s = q.toLowerCase();
            items = items.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.project_type || "").toLowerCase().includes(s));
        }
        return items;
    }, [rows, q, statusFilter]);

    return (
        <div className="p-6 md:p-10" data-testid="page-projects">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="chip chip-navy">Forge Projects</div>
                    <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic">Projects</h1>
                    <div className="mt-2 text-sm text-muted-foreground">Convert deals to live projects. Track phases, budget, funding, team.</div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid="new-project-btn">
                        <Plus size={14} /> New Project
                    </button>
                </div>
            </div>

            {/* Filter row */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="w-full border border-border bg-background pl-8 pr-3 py-2 text-sm" data-testid="projects-search" />
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="projects-status-filter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="on_hold">On hold</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                </select>
                <div className="ml-auto flex gap-1 border border-border p-1">
                    <button onClick={() => setView("cards")} className={`p-1.5 ${view === "cards" ? "bg-secondary" : ""}`} data-testid="view-cards" aria-label="Card view"><LayoutGrid size={14} /></button>
                    <button onClick={() => setView("table")} className={`p-1.5 ${view === "table" ? "bg-secondary" : ""}`} data-testid="view-table" aria-label="Table view"><TableIcon size={14} /></button>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="mt-10 text-sm text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
                <EmptyState onNew={() => setShowNew(true)} />
            ) : view === "cards" ? (
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-cards">
                    {filtered.map((p) => <ProjectCard key={p.id} p={p} />)}
                </div>
            ) : (
                <div className="mt-6 border border-border" data-testid="projects-table">
                    <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr" }}>
                        <div>Name</div><div>Type</div><div>Phase</div><div>Progress</div><div>Budget</div>
                    </div>
                    {filtered.map((p) => (
                        <Link to={`/app/projects/${p.id}`} key={p.id} data-testid={`project-row-${p.id}`}
                            className="grid px-4 py-3 border-b border-border last:border-b-0 text-sm hover:bg-secondary/40" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr" }}>
                            <div className="font-medium truncate">{p.name}</div>
                            <div className="text-muted-foreground capitalize">{(p.project_type || "").replace(/_/g, " ")}</div>
                            <div className="text-muted-foreground capitalize">{(p.current_phase_key || "").replace(/_/g, " ")}</div>
                            <div><ProgressBar pct={p.completion_pct || 0} /></div>
                            <div>{NUMBER.format(p.total_budget || 0)}</div>
                        </Link>
                    ))}
                </div>
            )}

            {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); nav(`/app/projects/${id}`); }} />}
        </div>
    );
}

function ProjectCard({ p }) {
    return (
        <Link to={`/app/projects/${p.id}`} data-testid={`project-card-${p.id}`}
            className="group border border-border p-5 hover:shadow-[0_10px_30px_-15px_rgba(15,39,71,0.35)] transition-shadow bg-card">
            <div className="flex items-center justify-between">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_COLORS[p.status] || STATUS_COLORS.archived}`}>{p.status}</span>
                <span className="text-xs text-muted-foreground capitalize">{(p.project_type || "").replace(/_/g, " ")}</span>
            </div>
            <div className="mt-4 font-display text-xl italic tracking-tight truncate">{p.name}</div>
            <div className="mt-1 text-xs text-muted-foreground capitalize">Phase: {(p.current_phase_key || "not started").replace(/_/g, " ")}</div>
            <div className="mt-4">
                <ProgressBar pct={p.completion_pct || 0} />
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{Math.round(p.completion_pct || 0)}% complete</span>
                    <span>{NUMBER.format(p.total_budget || 0)}</span>
                </div>
            </div>
        </Link>
    );
}

function ProgressBar({ pct }) {
    return (
        <div className="h-1.5 bg-secondary rounded-sm overflow-hidden" data-testid="progress-bar">
            <div className="h-full bg-navy" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
        </div>
    );
}

function EmptyState({ onNew }) {
    return (
        <div className="mt-16 border border-dashed border-border p-12 text-center" data-testid="projects-empty">
            <div className="mx-auto h-12 w-12 border border-border flex items-center justify-center"><HardHat size={20} /></div>
            <div className="mt-5 font-display text-2xl italic">No projects yet.</div>
            <div className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                Convert an approved deal to a project, or start a new project from a template.
            </div>
            <div className="mt-6 flex justify-center gap-3">
                <Link to="/app/deals" className="border border-border px-4 py-2 text-sm hover:bg-secondary" data-testid="empty-go-deals">Go to Deals</Link>
                <button onClick={onNew} className="bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90" data-testid="empty-new-project">Start new project</button>
            </div>
        </div>
    );
}

export function NewProjectModal({ onClose, onCreated, deal = null, defaultTemplateKey = null }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState(defaultTemplateKey || "");
    const [name, setName] = useState(deal?.name || "");
    const [plannedStart, setPlannedStart] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            const url = deal?.deal_type ? `/project-templates?deal_type=${deal.deal_type}` : "/project-templates";
            const { data } = await api.get(url);
            setTemplates(data.items || []);
            if (!templateId) {
                const preferred = data.suggested_key || (data.items || [])[0]?.key;
                if (preferred) setTemplateId(preferred);
            }
        };
        load();
        // eslint-disable-next-line
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!templateId) { toast.error("Pick a template"); return; }
        if (!name) { toast.error("Enter a project name"); return; }
        setSaving(true);
        try {
            if (deal) {
                const { data } = await api.post(`/deals/${deal.id}/convert-to-project`, {
                    template_id: templateId, project_name: name, planned_start: plannedStart || undefined,
                });
                toast.success("Project created from deal");
                onCreated?.(data.project.id);
            } else {
                const { data } = await api.post("/projects", {
                    template_id: templateId, name, planned_start: plannedStart || undefined,
                });
                toast.success("Project created");
                onCreated?.(data.id);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create");
        } finally { setSaving(false); }
    };

    const selected = templates.find((t) => t.key === templateId || t.id === templateId);

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="new-project-modal">
            <div className="bg-background border border-border w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="chip chip-navy">{deal ? "Convert deal → project" : "New Project"}</div>
                        <div className="mt-2 font-display text-2xl italic">{deal ? `Instantiate: ${deal.name}` : "Start a project from a template"}</div>
                    </div>
                    <button onClick={onClose} className="text-sm text-muted-foreground hover:opacity-70" data-testid="modal-close">Close</button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-5">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Template</label>
                        <div className="grid md:grid-cols-2 gap-2" data-testid="template-picker">
                            {templates.map((t) => (
                                <button type="button" key={t.key} onClick={() => setTemplateId(t.key)}
                                    className={`text-left border p-3 transition-colors ${templateId === t.key ? "border-navy bg-secondary" : "border-border hover:border-navy/40"}`}
                                    data-testid={`template-opt-${t.key}`}>
                                    <div className="text-sm font-medium">{t.name}</div>
                                    <div className="mt-1 text-[11px] text-muted-foreground">{(t.phases || []).length} phases · {(t.phases || []).reduce((n, p) => n + (p.tasks || []).length, 0)} tasks</div>
                                </button>
                            ))}
                        </div>
                        {selected?.description && <div className="mt-2 text-xs text-muted-foreground">{selected.description}</div>}
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Project name</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-border bg-background px-3 py-2 text-sm" data-testid="project-name-input" required />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Planned start (optional)</label>
                        <input type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="project-start-input" />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="border border-border px-4 py-2 text-sm" data-testid="modal-cancel">Cancel</button>
                        <button type="submit" disabled={saving} className="bg-navy text-arch-white px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50" data-testid="modal-submit">
                            {saving ? "Creating…" : (deal ? "Convert" : "Create Project")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
